import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import QRCode from "qrcode";
import { QrCode, Printer, CheckCircle2, Circle } from "lucide-react";

const FILTERS = [
  { v: "", label: "Todos" },
  { v: "free", label: "Livres" },
  { v: "assigned", label: "Vinculados" },
];

export default function QrBankTab() {
  const [data, setData] = useState({ total: 0, free: 0, assigned: 0, items: [] });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async (st = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/qr-pool", { params: st ? { status: st } : {} });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar o banco de QR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const printSheet = async () => {
    const items = data.items;
    if (!items.length) { toast.error("Nenhum código para imprimir com esse filtro."); return; }
    setPrinting(true);
    try {
      const origin = window.location.origin;
      const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const cells = await Promise.all(items.map(async (it) => {
        const url = `${origin}/eq/${it.code}`;
        const img = await QRCode.toDataURL(url, { width: 220, margin: 1 });
        return `<div class="cell"><img src="${img}" /><div class="code">${esc(it.code)}</div><div class="brand">Clima Branca</div></div>`;
      }));
      const w = window.open("", "_blank");
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>QR Codes Clima Branca</title>
        <style>
          *{box-sizing:border-box} body{font-family:Arial,sans-serif;margin:12px}
          h1{font-size:16px;color:#0a2a6c;margin:0 0 12px}
          .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
          .cell{border:1px dashed #94a3b8;border-radius:8px;padding:8px;text-align:center;page-break-inside:avoid}
          .cell img{width:100%;max-width:150px;height:auto}
          .code{font-weight:bold;font-size:16px;letter-spacing:1px;color:#0f172a;margin-top:2px}
          .brand{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:1px}
          @media print{ h1{display:none} }
        </style></head><body>
        <h1>Clima Branca · Etiquetas de QR (${items.length})</h1>
        <div class="grid">${cells.join("")}</div>
        <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
        </body></html>`);
      w.document.close();
    } catch (err) {
      toast.error("Falha ao gerar as etiquetas.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="qr-bank-tab">
      <div className="grid grid-cols-3 gap-3">
        {[["Total", data.total, "text-slate-800"], ["Livres", data.free, "text-emerald-600"], ["Vinculados", data.assigned, "text-[#1e7be0]"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4 text-center">
            <p className={`text-2xl font-bold ${c}`} data-testid={`qr-stat-${l.toLowerCase()}`}>{v}</p>
            <p className="text-xs text-slate-500">{l}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => setStatus(f.v)} data-testid={`qr-filter-${f.v || "all"}`}
              className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${status === f.v ? "bg-white text-[#0a2a6c] shadow-sm" : "text-slate-500"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <Button onClick={printSheet} disabled={printing} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="qr-print-button">
          <Printer className="h-4 w-4" /> {printing ? "Gerando..." : `Imprimir (${data.items.length})`}
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="qr-list">
          {data.items.map((it) => {
            const assigned = it.status === "assigned";
            return (
              <div key={it.code} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`qr-item-${it.code}`}>
                <QrCode className="h-6 w-6 text-slate-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800">{it.code}</p>
                  {assigned && it.equipment ? (
                    <p className="text-xs text-slate-500 truncate">{it.equipment.label} · {it.equipment.owner || "—"}{it.equipment.location ? ` (${it.equipment.location})` : ""}</p>
                  ) : (
                    <p className="text-xs text-emerald-600">Livre</p>
                  )}
                </div>
                {assigned
                  ? <CheckCircle2 className="h-4 w-4 text-[#1e7be0] flex-shrink-0" />
                  : <Circle className="h-4 w-4 text-emerald-400 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
